import React, { useCallback, useState } from 'react';
import { Grid } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { TimeSignal } from 'graphql/types';
import { ExpandCollapseAllButton } from 'modules/common/components/ExpandCollapseAllButton';
import { useDialogState, useFeatureFlag } from 'modules/common/hooks';
import { TimesheetLabelSuggestionContextProvider } from 'modules/timesheet-labels';
import { EditNoteDialog } from 'modules/notes/addNote/EditNoteDialog';
import { useNotesFilterContext } from '../NotesFilterContextProvider';
import { NotesSelectorCounter } from '../notesSelector/components';
import TimeSignalActionsByCategory, {
  BulkActions
} from '../notesSelector/components/TimeSignalActionsByCategory';
import { NotesTotalDuration, useGlobalDuration } from '../NotesTotalDuration';
import { SplitNoteDialog } from '../splitNote';
import { MultiEditDialog } from '../multiEdit';
import { MergeDialog } from '../merge';

import { getFullHoursMinutesMaskDuration } from '../common';
import {
  useBulkActionCallback,
  useNotesSelectionReset,
  useNotesSelectionCount
} from './hooks';

interface NotesListHeaderProps {
  notes: TimeSignal[];
  totalNotesCount: number;
  onExpandCollapseAll: (state: boolean) => void;
  expandCollapseAllId: string;
  hasVerticalScroll: boolean;
}

const useStyles = makeStyles((theme) => ({
  timesignalItem: {
    marginLeft: 'auto',
    marginRight: theme.spacing(5)
  },
  totalItem: {
    display: 'flex'
  },
  grid: {
    minHeight: theme.spacing(5),
    padding: ({
      hasVerticalScroll,
      showCollapseAll
    }: {
      hasVerticalScroll: boolean;
      showCollapseAll: boolean;
    }) => {
      let paddingR = hasVerticalScroll ? 2.375 : 1;
      if (!showCollapseAll) {
        paddingR += 5.2;
      }
      return theme.spacing(0, paddingR, 0, 1);
    }
  }
}));

export const NotesListHeader: React.FC<NotesListHeaderProps> = ({
  notes,
  totalNotesCount,
  onExpandCollapseAll,
  expandCollapseAllId,
  hasVerticalScroll
}: NotesListHeaderProps) => {
  const { filterContext } = useNotesFilterContext();
  const selectionCount = useNotesSelectionCount();
  const { resetForm } = useNotesSelectionReset();
  const {
    bulkActionCallback,
    isActionInProgress,
    filteredNotes
  } = useBulkActionCallback({
    notes
  });

  const [dialogContext, setDialogContext] = useState<
    'split' | 'multi-edit' | 'merge'
  >();

  const showCollapseAll = useFeatureFlag('isShowCollapseAll', false);
  const classes = useStyles({ hasVerticalScroll, showCollapseAll });
  const {
    open: isDialogOpen,
    openDialog: openNoteDialog,
    closeDialog: closeNoteDialog
  } = useDialogState();

  const actionCallback = useCallback(
    (action: BulkActions) => {
      if (action === 'split' || action === 'multi-edit' || action === 'merge') {
        setDialogContext(action);
        openNoteDialog();
      } else {
        bulkActionCallback(action);
      }
    },
    [bulkActionCallback, openNoteDialog]
  );

  const onDialogClose = useCallback(() => {
    closeNoteDialog();
    resetForm();
  }, [closeNoteDialog, resetForm]);
  const duration = useGlobalDuration({ notes });
  const ariaLabelDuration = getFullHoursMinutesMaskDuration(duration || 0);

  return (
    <>
      <Grid
        data-testid="notes-selector"
        container
        justifyContent={'space-between'}
        alignItems={'center'}
        className={classes.grid}
      >
        <Grid item>
          <NotesSelectorCounter
            notes={notes}
            totalCount={totalNotesCount}
            duration={ariaLabelDuration}
          />
        </Grid>
        <Grid item className={classes.timesignalItem}>
          {Boolean(selectionCount || isActionInProgress) && (
            <TimeSignalActionsByCategory
              category={filterContext.selectedClassification}
              actionCallback={actionCallback}
              selectedCount={selectionCount}
              loading={isActionInProgress}
            />
          )}
        </Grid>
        <Grid item className={classes.totalItem}>
          <NotesTotalDuration notes={notes} />
          {showCollapseAll && (
            <ExpandCollapseAllButton
              id={expandCollapseAllId}
              onExpandCollapseAll={onExpandCollapseAll}
            />
          )}
        </Grid>
      </Grid>
      {dialogContext === 'split' && isDialogOpen && filteredNotes[0] && (
        <SplitNoteDialog
          open={isDialogOpen}
          onClose={onDialogClose}
          timeSignal={filteredNotes[0]}
        />
      )}
      {dialogContext === 'multi-edit' &&
        isDialogOpen &&
        Boolean(filteredNotes.length) && (
          <TimesheetLabelSuggestionContextProvider
            disableSuggestions={selectionCount > 1}
            variant={selectionCount > 1 ? 'multi-edit' : 'default'}
          >
            {selectionCount > 1 ? (
              <MultiEditDialog
                open={isDialogOpen}
                onClose={onDialogClose}
                timeSignals={filteredNotes}
              />
            ) : (
              <EditNoteDialog
                open={isDialogOpen}
                onClose={onDialogClose}
                timeSignal={filteredNotes[0]}
              />
            )}
          </TimesheetLabelSuggestionContextProvider>
        )}
      {dialogContext === 'merge' &&
        isDialogOpen &&
        Boolean(filteredNotes.length) && (
          <TimesheetLabelSuggestionContextProvider>
            <MergeDialog
              open={isDialogOpen}
              onClose={onDialogClose}
              notes={filteredNotes}
            />
          </TimesheetLabelSuggestionContextProvider>
        )}
    </>
  );
};
